import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import AuthContext from "../../contexts/AuthProvider";
import { useAIChatFilters } from "../../contexts/AIChatFiltersContext";
import { client } from "../../services/axiosClient";
import "moment/locale/es";
import "moment/locale/pt";
import cx from "classnames";
import { Slide, Slide as ToastSlide, toast } from "react-toastify";
import { ThreeDots } from "react-loader-spinner";
import { useImmer } from "use-immer";
import { useTranslation } from "react-i18next";
import useWindowDimensions from "../../hooks/useWindowDimensions";
import { useScenarios } from "../../hooks/useScenarios";
import ScenarioControls from "../../components/ScenarioControls/ScenarioControls";
import { useScreenshot, createFileName } from "use-react-screenshot";
import { calculateOomFromPlValues, getOom } from "../../utils/utils";
import { computeInitialDrillDowns } from "../../utils/plUtils";

import { library } from "@fortawesome/fontawesome-svg-core";
import {
  faArrowRightArrowLeft,
  faArrowTrendDown,
  faArrowTrendUp,
  faCaretDown,
  faCaretUp,
  faChevronDown,
  faChevronUp,
  faClone,
  faFilter,
  faPenToSquare,
  faX,
} from "@fortawesome/free-solid-svg-icons";

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import Collapse from "@material-ui/core/Collapse";
import Hidden from "@material-ui/core/Hidden";

// @material-ui/icons
import {
  AttachMoney,
  Close,
  FormatListNumbered,
  HelpOutline,
  Info,
  Layers,
  PhotoCamera,
  Traffic,
} from "@material-ui/icons";

// core components
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import ActionDropdown from "components/ActionDropdown/ActionDropdown";
import ActionBar from "components/ActionBar/ActionBar";
import HelpTooltip from "components/HelpTooltip/HelpTooltip";
import ActionBarButton from "../../components/ActionBar/ActionBarButton";
import FullPageLoader from "../../components/Loaders/FullPageLoader";
import Pnl from "../../components/Pnl/Pnl";
import PnlDimensionDetail from "../../components/Pnl/PnlDimensionDetail";
import { PnlSharedProvider } from "../../hooks/usePnlShared";
import ResultsFilterBar from "../../components/ResultsFilterBar";
import { useResultsFilters } from "../../hooks/useResultsFilters";
import DimFilterDialog from "../../components/ResultsFilterBar/DimFilterDialog";

import styles from "../../assets/jss/material-dashboard-pro-react/views/monthlyPLStyle";
import CardHeader from "../../components/Card/CardHeader";
import ChartistGraph from "react-chartist";
import useThemeColors from "../../hooks/useThemeColors";

import {
  linePercentageChartNoAnimation,
  lineChartNoAnimation,
} from "../../variables/charts";

library.add(
  faChevronUp,
  faChevronDown,
  faCaretUp,
  faCaretDown,
  faPenToSquare,
  faFilter,
  faClone,
  faArrowRightArrowLeft,
  faX,
  faArrowTrendUp,
  faArrowTrendDown,
);

const getIndexes = (filters, values) => {
  return values.map((item) => {
    return filters.indexOf(item);
  });
};

const getMedian = (arr) => {
  arr.sort((a, b) => a - b);
  const mid = Math.floor(arr.length / 2);
  if (arr.length % 2 === 0) {
    return (arr[mid - 1] + arr[mid]) / 2;
  } else {
    return arr[mid];
  }
};

export default function MonthlyPL() {
  // region initial declarations
  let { userDetails } = useContext(AuthContext);
  const { clientColor } = useThemeColors();
  const useStyles = makeStyles(styles);
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const { height } = useWindowDimensions();
  // endregion

  // region notify
  const notify = (text, type = "info", time = 3000) =>
    toast(t(text), {
      position: "top-right",
      autoClose: time,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: false,
      progress: undefined,
      theme: "light",
      type: type,
      transition: ToastSlide,
    });

  const notifyCopy = () =>
    toast(t("results.scenarios.url_sent_to_clipboard"), {
      position: "bottom-center",
      autoClose: 2000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: false,
      draggable: false,
      progress: undefined,
      theme: "light",
      type: "info",
      transition: Slide,
    });
  // endregion

  // region state declarations
  const [lastMonth, setLastMonth] = useState(null);
  const [monthsList, setMonthsList] = useState([]);
  const [chartLabels, setChartLabels] = useState([]);
  const volumeUnit = useRef(null);
  const [volumeColumn, setVolumeColumn] = useState("volume");
  const volumeUnitAlternatives = useRef([]);
  const currencyUnitOptions = useRef([]);
  const currencyUnitOptionsAvailable = useRef([]);
  const plStructure = useRef([]);
  const netRevenueAnchor = useRef(null);
  const [revenueReference, setRevenueReference] = useState(null);
  const revenueReferenceOptions = useRef([]);
  const combinedAboveAnchor = useRef(null);
  const serveMarginAnchor = useRef(null);
  const revenueAnchors = useRef([]);
  const volumeL12M = useRef(null);

  const metaLoadedRef = useRef(false);

  // Callback de meta para extrair dados não-filtro (lastMonth, plStructure, etc.)
  // Definido antes do hook para poder ser passado como opção.
  // Usa refs para evitar dependências circulares.
  const onMetaLoadedCallback = useRef(null);
  useEffect(() => {
    onMetaLoadedCallback.current = (response) => {
      const filters = response.data.filters;
      const lastMonthItem =
        filters.items.period?.[filters.items.period.length - 1];
      if (lastMonthItem) setLastMonth(lastMonthItem);
      const pl = response.data.pl;
      volumeUnit.current = pl.volume_unit;
      volumeUnitAlternatives.current = pl.volume_unit_alternatives || [];
      currencyUnitOptions.current = pl.currency_unit_options;
      currencyUnitOptionsAvailable.current = pl.currency_unit_options_available;
      plStructure.current = pl.pl_structure;
      netRevenueAnchor.current = pl.net_revenue_anchor;
      revenueReferenceOptions.current = pl.revenue_reference_options || [];
      combinedAboveAnchor.current =
        revenueReferenceOptions.current.length > 0
          ? revenueReferenceOptions.current[0].value
          : null;
      setRevenueReference((prev) => {
        if (prev !== null) return prev;
        const defaultOpt = revenueReferenceOptions.current.find(
          (o) => o.default,
        );
        return defaultOpt ? defaultOpt.value : pl.net_revenue_anchor;
      });
      serveMarginAnchor.current = pl.serve_margin_anchor;
      revenueAnchors.current = pl.revenue_anchors;
      volumeL12M.current = pl.volume_l12m;
      setDrillDownsOpen(computeInitialDrillDowns(pl.pl_structure, height));
      setCurrencyUnit((prev) => prev ?? pl.default_currency_unit);
      setPlsValues((draft) => {
        [...Array(12)].map((_, i) => (draft[i + 1] = {}));
      });
      scenarioHook.setScenariosFromMeta(response.data.scenarios);
      metaLoadedRef.current = true;
    };
  }); // runs every render to keep the callback fresh (refs prevent stale closure issues)

  // Filtros gerenciados pelo hook unificado (sem período: período = lastMonth)
  const {
    filtersList,
    filtersNames,
    filtersItems,
    filtersGroups,
    metaLoaded,
    commonFilters,
    setCommonFilters,
    setCommonFiltersIndexes,
    commonFiltersMode,
    setCommonFiltersMode,
    commonFiltersSequence,
    setCommonFiltersSequence,
    filterMenuClickHandler,
    removeFilter,
    dimFilterDialogProps,
    dimSections,
    persist,
    setPersist,
    handleResetFilters,
    rangeFilters,
    clearRangeFilter,
  } = useResultsFilters({
    includePeriod: false,
    onMetaLoaded: (response) => onMetaLoadedCallback.current?.(response),
    getDimInfoExtra: () => ({
      hasRefRange: true,
      plLines: plStructure.current.map((l) => ({
        key: l.meta_name,
        label: l.name,
        level: l.level,
        isParent: l.is_parent,
      })),
      serveMarginAnchor: serveMarginAnchor.current,
      revenueAnchors: revenueAnchors.current,
    }),
  });

  const [currencyUnit, setCurrencyUnit] = useState(null);

  // ── useScenarios — centralized scenario management ──────────────────────
  const scenarioHook = useScenarios({
    viewType: "monthly_pl",
    buildPayload: useCallback(
      () => ({
        commonFilters,
        commonFiltersMode,
        commonFiltersSequence,
        currencyUnit,
        lastMonth,
      }),
      [
        commonFilters,
        commonFiltersMode,
        commonFiltersSequence,
        currencyUnit,
        lastMonth,
      ],
    ),
    applyPayload: useCallback(
      (data) => {
        setCommonFilters(data.commonFilters);
        setCommonFiltersMode(data.commonFiltersMode);
        setCommonFiltersSequence(data.commonFiltersSequence);
        const indexes = {};
        filtersList.current.forEach((f) => {
          if (data.commonFiltersMode[f] === "group") {
            indexes[f] = getIndexes(
              Object.keys(filtersGroups.current[f] || {}),
              data.commonFilters[f] || [],
            );
          } else if (data.commonFiltersMode[f] === "items") {
            indexes[f] = getIndexes(
              filtersItems.current[f] || [],
              data.commonFilters[f] || [],
            );
          } else {
            indexes[f] = [];
          }
        });
        setCommonFiltersIndexes(indexes);
        if (data.currencyUnit) setCurrencyUnit(data.currencyUnit);
        if (data.lastMonth) setLastMonth(data.lastMonth);
        setPlsValues((draft) => {
          [...Array(12)].map((_, i) => (draft[i + 1] = {}));
        });
        refreshPL(
          data.commonFilters,
          data.commonFiltersMode,
          data.commonFiltersSequence,
          data.currencyUnit,
          data.lastMonth,
        );
      },
      [], // eslint-disable-line
    ),
    t,
    notify,
  });

  // Populate scenarios from meta + resolve URL scenario
  useEffect(() => {
    if (metaLoaded) {
      scenarioHook.initFromURL();
    }
  }, [metaLoaded]); // eslint-disable-line

  const [showValueType, setShowValueType] = useState("percentage");
  const [benchmark, setBenchmark] = useState("total_company");

  const [drillDownsOpen, setDrillDownsOpen] = useState([]);

  const [plsValues, setPlsValues] = useImmer({});
  const [lyPlsValues, setLyPlsValues] = useImmer({});

  const [pinnedDimension, setPinnedDimension] = useState(null);
  const [pinnedDimensionData, setPinnedDimensionData] = useImmer({});
  const [pinnedDimensionLoading, setPinnedDimensionLoading] = useState(false);
  const [pinnedDimensionLimit, setPinnedDimensionLimit] = useState(10);

  const [plLoading, setPlLoading] = useState(false);

  const [mouseOverPLLine, setMouseOverPLLine] = useState(null);
  const [mouseOverPLLineFade, setMouseOverPLLineFade] = useState(null);
  const [delayHandler, setDelayHandler] = useState(null);
  const [pinnedName, setPinnedName] = useState(null);
  const lastPlottedValue = useRef(null);
  const [contextMenuOpen, setContextMenuOpen] = useState(false);

  const [oom, setOom] = useState({
    div: 1e6,
    x: "&nbsp;" + t("oom.m"),
  });
  const [monthlyChart, setMonthlyChart] = useState({
    ...linePercentageChartNoAnimation,
    data: {
      labels: [],
      series: [[]],
    },
  });
  const [chartistKey, setChartistKey] = useState(0);

  const resultsRef = useRef(null);
  // endregion

  // region functions
  // eslint-disable-next-line no-unused-vars
  const [image, takeScreenShot] = useScreenshot({
    type: "image/png",
    quality: 1.0,
  });
  const download = (
    image,
    { name = "results_screenshot", extension = "png" } = {},
  ) => {
    const a = document.createElement("a");
    a.href = image;
    a.download = createFileName(extension, name);
    a.click();
  };
  const downloadScreenshot = () => {
    takeScreenShot(resultsRef.current).then(download);
  };
  const getMonthsListFromLastMonth = useMemo(() => {
    if (!lastMonth) return [];

    const [year, month] = lastMonth;
    let result = [];
    for (let i = 11; i >= 0; i--) {
      let newMonth = month - i;
      let newYear = year;
      if (newMonth <= 0) {
        newMonth += 12;
        newYear -= 1;
      }
      result.push(newYear * 100 + newMonth);
    }
    return result;
  }, [lastMonth]);
  const getUnitOptions = useMemo(() => {
    let options = [];
    // currencies
    currencyUnitOptions.current.map((opt) => {
      let available = currencyUnitOptionsAvailable.current.includes(opt);
      options.push({
        identified: {
          option_type: "currency",
          value: opt,
        },
        text:
          opt +
          (available
            ? ""
            : " (" +
              t("results.missing_rates") +
              (["DEV", "IMG", "CAD", "CFA", "CST"].includes(
                userDetails.user_role,
              )
                ? " - " + t("results.missing_rates_complement")
                : "") +
              ")"),
        active: opt === currencyUnit,
        disabled: !available,
      });
    });
    // revenue reference (only show if more than 1 option)
    if (revenueReferenceOptions.current.length > 1) {
      options.push({ divider: true });
      options.push({
        explainer: true,
        text: (
          <span>
            {t("results.unit_blocks.revenue_reference")}{" "}
            <HelpTooltip
              small
              tooltip={t("results.unit_blocks.revenue_reference_tooltip")}
            />
          </span>
        ),
      });
      revenueReferenceOptions.current.forEach((opt) => {
        options.push({
          identified: {
            option_type: "revenue_reference",
            value: opt.value,
          },
          text:
            opt.value === "combined" ? (
              <span>
                {t("results.unit_blocks.combined")}{" "}
                <HelpTooltip
                  small
                  tooltip={(() => {
                    const refs = revenueReferenceOptions.current.filter(
                      (o) => o.value !== "combined",
                    );
                    const first = refs[0]?.label || "";
                    const last = refs[refs.length - 1]?.label || "";
                    return `${first} ${t("results.unit_blocks.combined_above")} / ${last} ${t("results.unit_blocks.combined_below")}`;
                  })()}
                />
              </span>
            ) : (
              opt.label
            ),
          active: revenueReference === opt.value,
        });
      });
    }
    // volume unit alternatives (only show if more than 1 option)
    if (volumeUnitAlternatives.current.length > 1) {
      options.push({ divider: true });
      options.push({
        explainer: true,
        text: t("results.unit_blocks.volume"),
      });
      volumeUnitAlternatives.current.forEach((opt) => {
        options.push({
          identified: {
            option_type: "volume",
            value: opt.column,
          },
          text: opt.label,
          active: volumeColumn === opt.column,
        });
      });
    }
    options.push({ divider: true });
    // percent value
    options.push({
      identified: {
        option_type: "value_type",
        value: "percentage",
      },
      text: t("results.percent_value"),
      active: showValueType === "percentage",
    });
    // nominal value
    options.push({
      identified: {
        option_type: "value_type",
        value: "value",
      },
      text: t("results.nominal_value"),
      active: showValueType === "value",
    });
    // value / unit
    options.push({
      identified: {
        option_type: "value_type",
        value: "per_unit",
      },
      text: t("results.value") + " / " + volumeUnit.current,
      active: showValueType === "per_unit",
    });
    return options;
  }, [
    currencyUnit,
    revenueReference,
    showValueType,
    volumeColumn,
    volumeUnit.current,
    currencyUnitOptions.current,
    currencyUnitOptionsAvailable.current,
    userDetails.user_role,
    t,
  ]);
  const getBenchmarkOptions = useMemo(() => {
    let options = [];
    options.push({
      identified: {
        value: "total_company",
      },
      text: t("results.total_company"),
      active: benchmark === "total_company",
    });
    options.push({
      identified: {
        value: "first_pnl",
      },
      text: t("results.first_month"),
      active: benchmark === "first_pnl",
    });
    options.push({
      identified: {
        value: "external",
      },
      text: t("results.external"),
      active: benchmark === "external",
      disabled: true,
    });
    return options;
  }, [benchmark, t]);
  const {
    setPageFilters: _setChatFilters,
    clearPageFilters: _clearChatFilters,
  } = useAIChatFilters();
  // Atualiza filtros para o AI Chat sempre que o estado muda
  useEffect(() => {
    const summary = [];
    commonFiltersSequence.forEach((filterName) => {
      const items = commonFilters[filterName] || [];
      if (items.length === 0) return;
      const label =
        filtersNames.current[filterName]?.[i18n.language] || filterName;
      summary.push({ dimension: label, values: items });
    });
    _setChatFilters("Monthly PL", summary);
    return () => _clearChatFilters();
  }, [commonFilters, commonFiltersMode, commonFiltersSequence, i18n.language]);

  // Function to build filter scope data for the opportunity dialog
  const buildFilterScopeData = useCallback(() => {
    const scopeData = {
      viewType: "monthly_pl",
    };

    // Process each P&L (MonthlyPL has 12 P&Ls, one for each month)
    for (let numPL = 1; numPL <= 12; numPL++) {
      const activeFilters = [];

      // Add common filters (excluding period filter as it's handled centrally in Pnl)
      commonFiltersSequence
        .filter((f) => Object.keys(filtersNames.current).includes(f))
        .forEach((filterName) => {
          if (
            filterName !== "period" &&
            commonFilters[filterName]?.length > 0
          ) {
            activeFilters.push({
              name:
                filtersNames.current[filterName]?.[i18n.language] || filterName,
              type: "common",
              filterName: filterName,
              items: commonFilters[filterName],
            });
          }
        });

      scopeData[numPL.toString()] = activeFilters;
    }

    return scopeData;
  }, [
    commonFilters,
    commonFiltersMode,
    commonFiltersSequence,
    filtersNames,
    i18n.language,
  ]);

  // Memoize the filter scope data to prevent unnecessary re-renders
  const memoizedFilterScopeData = useMemo(
    () => buildFilterScopeData(),
    [commonFilters, commonFiltersMode, commonFiltersSequence, i18n.language],
  );
  const refreshPL = (
    common_filters = commonFilters,
    common_filters_mode = commonFiltersMode,
    common_filters_sequence = commonFiltersSequence,
    currency_unit = currencyUnit,
    last_month = lastMonth,
    volume_column = volumeColumn,
  ) => {
    if (!metaLoadedRef.current) return false; // avoids calling API during load
    let controller = new AbortController();
    let params = {
      currency_unit,
      common_filters,
      common_filters_mode,
      common_filters_sequence,
      ref_range_filters:
        Object.keys(rangeFilters).length > 0
          ? Object.entries(rangeFilters).map(([dim, f]) => ({
              dimension: dim,
              mode: f.mode,
              min: f.min,
              max: f.max,
              ref_line: f.refLine,
            }))
          : null,
      ref_color_line:
        Object.keys(rangeFilters).length > 0
          ? Object.values(rangeFilters)[0]?.refLine || null
          : null,
      volume_column,
      last_month,
      revenue_reference: revenueReference,
    };
    setPlLoading(true);
    client
      .post("api/results_monthly_pl_read_pls/", params, {
        signal: controller.signal,
      })
      .then((response) => {
        const months_list = getMonthsListFromLastMonth;
        const ly_months_list = months_list.map((x) => x - 100);
        setPlsValues((draft) => {
          [...Array(12)].map(
            (_, i) => (draft[i + 1] = response.data.pls[months_list[i]]),
          );
        });
        setLyPlsValues((draft) => {
          [...Array(12)].map(
            (_, i) => (draft[i + 1] = response.data.pls[ly_months_list[i]]),
          );
        });
        setMonthsList(Object.keys(response.data.pls));
        setPlLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setPlLoading(false);
      });
  };
  const fetchDimensionDetail = (dimension, limit = pinnedDimensionLimit) => {
    if (!pinnedName || !dimension) return;

    setPinnedDimensionData({});
    setPinnedDimensionLoading(true);

    let params = {
      pinned_line: pinnedName,
      dimension: dimension,
      currency_unit: currencyUnit,
      common_filters: commonFilters,
      common_filters_mode: commonFiltersMode,
      common_filters_sequence: commonFiltersSequence,
      last_month: lastMonth,
      volume_column: volumeColumn,
      limit: limit,
    };

    client
      .post("api/results_monthly_pl_dimension_detail/", params)
      .then((response) => {
        setPinnedDimensionData(response.data.dimension_values);
        setPinnedDimensionLoading(false);
      })
      .catch((error) => {
        console.log(error);
        notify("Error loading dimension detail", "error");
        setPinnedDimensionLoading(false);
      });
  };
  // endregion

  // region useEffect's
  useEffect(() => {
    if (pinnedName && pinnedDimension) {
      fetchDimensionDetail(pinnedDimension, pinnedDimensionLimit);
    }
  }, [pinnedName, pinnedDimensionLimit]);

  useEffect(() => {
    i18n.changeLanguage(i18n.language.slice(0, 2));
  }, []); // eslint-disable-line
  // Dispara refreshPL após meta carregar e a cada mudança de filtros
  useEffect(() => {
    if (!metaLoaded) return;
    refreshPL();
    // eslint-disable-line
  }, [metaLoaded, commonFilters, commonFiltersMode, commonFiltersSequence]);
  useEffect(() => {
    let months = [];
    monthsList.slice(-12).forEach((month) => {
      let y = Math.floor(parseInt(month) / 100) % 100;
      let m = parseInt(month) % 100;
      months.push(`${t("months." + m)}-${y}`);
    });
    setChartLabels(months);
    setMonthlyChart({
      ...monthlyChart,
      data: {
        ...monthlyChart.data,
        labels: months,
      },
    });
  }, [monthsList, i18n.language]);
  useEffect(() => {
    let values = [];
    let lyValues = [];
    let meta_name;
    let refresh = false;
    if (pinnedName !== null) {
      meta_name = pinnedName;
    } else if (mouseOverPLLine) {
      meta_name = mouseOverPLLine;
    }
    if (meta_name) {
      if (lastPlottedValue.current !== meta_name) {
        let activeAnchor;
        if (revenueReference === "combined") {
          const lineIdx = plStructure.current.findIndex(
            (l) => l.meta_name === meta_name,
          );
          const netIdx = plStructure.current.findIndex(
            (l) => l.meta_name === netRevenueAnchor.current,
          );
          activeAnchor =
            lineIdx < netIdx
              ? combinedAboveAnchor?.current
              : netRevenueAnchor.current;
        } else {
          activeAnchor = revenueReference || netRevenueAnchor.current;
        }
        Object.values(plsValues).forEach((val) => {
          if (val !== undefined) {
            if (showValueType === "percentage") {
              values.push(val[meta_name] / val[activeAnchor]);
            } else if (showValueType === "value") {
              values.push(val[meta_name] / oom.div);
            } else if (showValueType === "per_unit") {
              values.push(val[meta_name] / val.volume / oom.div);
            }
          }
        });
        Object.values(lyPlsValues).forEach((val) => {
          if (val === undefined) {
            lyValues.push(null);
          } else {
            if (showValueType === "percentage") {
              lyValues.push(val[meta_name] / val[activeAnchor]);
            } else if (showValueType === "value") {
              lyValues.push(val[meta_name] / oom.div);
            } else if (showValueType === "per_unit") {
              lyValues.push(val[meta_name] / val.volume / oom.div);
            }
          }
        });
        refresh = true;
        lastPlottedValue.current = meta_name;
      }
    } else {
      refresh = true;
      lastPlottedValue.current = null;
    }
    if (refresh) {
      setMonthlyChart({
        ...monthlyChart,
        data: {
          labels: chartLabels,
          series: [values, lyValues],
        },
      });
    }
  }, [pinnedName, mouseOverPLLine, plsValues, chartLabels, i18n.language]);
  useEffect(() => {
    let data = {
      labels: [],
      series: [[]],
    };
    if (showValueType === "percentage") {
      setMonthlyChart({
        ...linePercentageChartNoAnimation,
        data: data,
      });
    } else {
      setMonthlyChart({
        ...lineChartNoAnimation,
        options: {
          ...lineChartNoAnimation.options,
          chartPadding: {
            ...lineChartNoAnimation.options.chartPadding,
            left: 25,
          },
          axisY: {
            labelInterpolationFnc: function (value) {
              return value.toFixed(1) + oom.x;
            },
          },
        },
        data: data,
      });
    }
    setChartistKey((prev) => prev + 1); // force chart re-render
    setPinnedName(null);
  }, [showValueType, oom]);
  useLayoutEffect(() => {
    // changes the order of magnitude in which all the P&Ls are presented
    //    - fired when the plValues or language is modified
    //    - store results in 'oom' object
    let median;
    if (showValueType === "value") {
      median = getMedian(
        Object.values(plsValues)
          .filter((vals) => vals !== undefined)
          .map((vals) => vals[serveMarginAnchor.current]),
      );
    } else if (showValueType === "per_unit") {
      median = getMedian(
        Object.values(plsValues)
          .filter((vals) => vals !== undefined)
          .map((vals) => vals[serveMarginAnchor.current] / vals.volume),
      );
    }
    if (median) {
      let { div, x } = getOom(median);
      x = x === "" ? x : " " + t("oom." + x);
      setOom({ div: div, x: x });
    } else {
      // Use common function for auto OOM calculation
      const calculatedOom = calculateOomFromPlValues(plsValues, t);
      setOom(calculatedOom);
    }
  }, [plsValues, showValueType, i18n.language]);
  // endregion

  // region handlers
  const handleUnitSelection = (opt) => {
    const { option_type, value } = opt;
    if (option_type === "value_type") {
      setShowValueType(value);
    } else if (option_type === "currency") {
      if (value !== currencyUnit) {
        setCurrencyUnit(value);
        refreshPL(
          commonFilters,
          commonFiltersMode,
          commonFiltersSequence,
          value,
          lastMonth,
        );
      }
    } else if (option_type === "revenue_reference") {
      setRevenueReference(value);
    } else if (option_type === "volume") {
      if (value !== volumeColumn) {
        setVolumeColumn(value);
        const selectedAlt = volumeUnitAlternatives.current.find(
          (o) => o.column === value,
        );
        if (selectedAlt) {
          volumeUnit.current = selectedAlt.label;
        }
        refreshPL(
          commonFilters,
          commonFiltersMode,
          commonFiltersSequence,
          currencyUnit,
          lastMonth,
          value,
        );
      }
    }
  };
  const handleBenchmarkSelection = (opt) => {
    const { value } = opt;
    setBenchmark(value);
  };
  const handleContextMenuStateChange = useCallback((isOpen) => {
    setContextMenuOpen(isOpen);

    // When context menu closes, clear the hover state after a short delay
    if (!isOpen) {
      setTimeout(() => {
        setMouseOverPLLine(null);
        setMouseOverPLLineFade(null);
      }, 100);
    }
  }, []);

  const handleMouseOverPLLine = useCallback(
    (meta_name) => {
      if (pinnedName === null) {
        setMouseOverPLLine(meta_name);
        setDelayHandler(
          setTimeout(() => {
            setMouseOverPLLineFade(meta_name);
          }, 500),
        );
      }
    },
    [pinnedName],
  );

  const handleMouseLeavePLLine = useCallback(() => {
    // Don't clear hover state if context menu is open
    if (!contextMenuOpen) {
      clearTimeout(delayHandler);
      setMouseOverPLLine(null);
      setMouseOverPLLineFade(null);
    }
  }, [contextMenuOpen, delayHandler]);
  // endregion

  // region components
  const getPL = (numPL) => {
    if (metaLoadedRef.current) {
      let year_month = 0;
      let name = t("sidebar_cts.results_menu.monthly_pl");
      if (numPL > 0) {
        year_month = getMonthsListFromLastMonth[numPL - 1];
        let year = (Math.floor(year_month / 100) % 100).toString();
        let month = (year_month % 100).toString();
        name = `${t("months." + month)}-${year}`;
      }
      if (numPL === 0 || plsValues[numPL] !== undefined) {
        return (
          <GridItem
            sm={numPL === 0 ? "auto" : true}
            key={numPL}
            zeroMinWidth
            style={{ padding: "0 1px" }}
            className={cx({
              [classes.plDisplay]: true,
              show: true,
            })}
          >
            <Card style={{ marginTop: 0, marginBottom: 18 }}>
              <CardBody style={{ padding: 0 }}>
                <Pnl
                  plStructure={plStructure}
                  netRevenueAnchor={netRevenueAnchor}
                  combinedAboveAnchor={combinedAboveAnchor}
                  revenueReference={revenueReference}
                  serveMarginAnchor={serveMarginAnchor}
                  revenueAnchors={revenueAnchors.current}
                  numPL={numPL}
                  numPLs={14}
                  plName={name}
                  handleMouseLeavePLLine={handleMouseLeavePLLine}
                  handleMouseOverPLLine={handleMouseOverPLLine}
                  mouseOverPLLine={mouseOverPLLine}
                  mouseOverPLLineFade={mouseOverPLLineFade}
                  drillDownsOpen={drillDownsOpen}
                  setDrillDownsOpen={setDrillDownsOpen}
                  timersAllowed={true}
                  retryPL={null}
                  isRefreshing={plLoading}
                  plValues={plsValues}
                  volumeUnit={volumeUnit}
                  volumeL12M={volumeL12M}
                  currencyUnit={currencyUnit}
                  showValueType={showValueType}
                  isLoading={!metaLoaded}
                  nameOnly
                  showColumns={
                    numPL === 0
                      ? ["name"]
                      : showValueType === "percentage"
                        ? ["percentage"]
                        : ["val"]
                  }
                  nameAlignRight={numPL > 0}
                  benchmark={benchmark}
                  showPin={numPL === 0}
                  pinnedName={pinnedName}
                  setPinnedName={setPinnedName}
                  oom={oom}
                  filterScopeData={memoizedFilterScopeData}
                  onContextMenuStateChange={handleContextMenuStateChange}
                  showValueTooltip={true}
                />
              </CardBody>
            </Card>
          </GridItem>
        );
      } else {
        return null;
      }
    } else {
      return null;
    }
  };
  const PLs = useMemo(
    () => [...Array(13)].map((_, key) => getPL(key)),
    [
      metaLoadedRef.current,
      lastMonth,
      plsValues,
      plStructure,
      netRevenueAnchor,
      serveMarginAnchor,
      revenueAnchors.current,
      mouseOverPLLine,
      mouseOverPLLineFade,
      drillDownsOpen,
      plLoading,
      volumeUnit,
      volumeL12M,
      currencyUnit,
      showValueType,
      metaLoaded,
      benchmark,
      pinnedName,
      contextMenuOpen,
      memoizedFilterScopeData,
      handleMouseLeavePLLine,
      handleMouseOverPLLine,
      t,
    ],
  );
  const plActions = (
    <>
      {/* Scenarios */}
      <ScenarioControls
        scenarioHook={scenarioHook}
        disabled={!metaLoaded}
        classes={classes}
        notifyCopy={notifyCopy}
      />
      {/* Unit */}
      <ActionDropdown
        button={
          <ActionBarButton
            icon={<AttachMoney />}
            text={t("results.unit")}
            disabled={!metaLoaded}
          />
        }
        dropdownList={getUnitOptions}
        onClick={handleUnitSelection}
      />
      {/* Benchmarks */}
      <ActionDropdown
        button={
          <ActionBarButton
            icon={<Traffic />}
            text={t("results.benchmark")}
            disabled={!metaLoaded}
          />
        }
        dropdownList={getBenchmarkOptions}
        onClick={handleBenchmarkSelection}
      />
      {/* Take photo */}
      <ActionBarButton
        icon={<PhotoCamera />}
        text={t("results.take_photo")}
        onClickHandler={downloadScreenshot}
        disabled={!metaLoaded}
      />
      {/* Help */}
      <ActionBarButton
        icon={<HelpOutline />}
        text={t("results.help")}
        disabled={true}
      />
    </>
  );
  // endregion

  return (
    <>
      <div>
        <Hidden smDown>
          <ActionBar
            actions={plActions}
            spinnerIn={!metaLoaded || scenarioHook.busy}
          />
        </Hidden>
        <div ref={resultsRef}>
          <GridContainer style={{ marginTop: "-28px" }}>
            <Hidden mdUp>
              <GridItem
                xs={12}
                // onMouseEnter={() => clearTimeouts()}
              >
                <ActionBar
                  actions={plActions}
                  spinnerIn={!metaLoaded || scenarioHook.busy}
                  sm
                />
              </GridItem>
            </Hidden>
            <ResultsFilterBar
              filtersList={filtersList}
              filtersNames={filtersNames}
              filtersGroups={filtersGroups}
              commonFilters={commonFilters}
              commonFiltersMode={commonFiltersMode}
              commonFiltersSequence={commonFiltersSequence}
              filterMenuClickHandler={filterMenuClickHandler}
              removeFilter={removeFilter}
              dimFilterDialogProps={dimFilterDialogProps}
              clientColor={clientColor}
              sections={dimSections}
              showPeriodExtras
              periodExtrasProps={{
                hidePeriod: true,
                persist,
                onPersistChange: setPersist,
                onReset: handleResetFilters,
              }}
              rangeFilters={rangeFilters}
              clearRangeFilter={clearRangeFilter}
            />
            <DimFilterDialog
              {...dimFilterDialogProps}
              clientColor={clientColor}
            />
            <GridItem xs={12}>
              <PnlSharedProvider
                plValues={plsValues}
                plStructure={plStructure}
                benchmark={benchmark}
                netRevenueAnchor={netRevenueAnchor}
                combinedAboveAnchor={combinedAboveAnchor}
                revenueReference={revenueReference}
                serveMarginAnchor={serveMarginAnchor}
                oom={oom}
                t={t}
              >
                <GridContainer className={classes.plWrapperOuter}>
                  {PLs}
                </GridContainer>
              </PnlSharedProvider>
            </GridItem>
          </GridContainer>
        </div>
        <GridContainer>
          <GridItem xs={12}>
            {metaLoaded && Object.keys(plsValues).length > 0 && (
              <Card chart className={classes.cardHover}>
                <CardHeader
                  color={
                    userDetails.implementation_tone === "color"
                      ? "danger"
                      : userDetails.implementation_tone === "gray"
                        ? "gray"
                        : "client"
                  }
                  className={classes.cardHeaderHover}
                >
                  <ChartistGraph
                    key={chartistKey}
                    className="ct-chart-white-colors"
                    data={monthlyChart.data}
                    type="Line"
                    options={monthlyChart.options}
                    listener={monthlyChart.animation}
                  />
                </CardHeader>
                <CardBody>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <h4
                      className={cx({
                        [classes.cardTitle]: true,
                        [classes.cardTitleWaiting]:
                          !mouseOverPLLine && !pinnedName,
                      })}
                    >
                      {mouseOverPLLine || pinnedName
                        ? plStructure.current.find(
                            (item) =>
                              item.meta_name === mouseOverPLLine ||
                              item.meta_name === pinnedName,
                          )?.name
                        : t("results.mouse_over_line")}
                      {!metaLoaded && (
                        <ThreeDots
                          color={clientColor}
                          width={"50px"}
                          height={"50px"}
                          wrapperStyle={{
                            marginTop: "-15px",
                            marginBottom: "-10px",
                          }}
                          ariaLabel="loading"
                        />
                      )}
                    </h4>
                    {Object.values(lyPlsValues).some(
                      (x) => x !== undefined,
                    ) && (
                      <div
                        className={classes.graphLegend}
                        style={{ textAlign: "right" }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            marginBottom: "4px",
                          }}
                        >
                          <svg
                            width="40"
                            height="12"
                            style={{ marginRight: "8px" }}
                          >
                            <circle cx="4" cy="6" r="3" fill="#333333" />
                            <line
                              x1="7"
                              y1="6"
                              x2="33"
                              y2="6"
                              stroke="#333333"
                              strokeWidth="2"
                            />
                            <circle cx="36" cy="6" r="3" fill="#333333" />
                          </svg>
                          <span>{t("results.solid_line_legend")}</span>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                          }}
                        >
                          <svg
                            width="40"
                            height="12"
                            style={{ marginRight: "8px" }}
                          >
                            <circle cx="4" cy="6" r="3" fill="#aaaaaa" />
                            <line
                              x1="7"
                              y1="6"
                              x2="33"
                              y2="6"
                              stroke="#aaaaaa"
                              strokeWidth="2"
                              strokeDasharray="2,3"
                            />
                            <circle cx="36" cy="6" r="3" fill="#aaaaaa" />
                          </svg>
                          <span>{t("results.dotted_line_legend")}</span>
                        </div>
                      </div>
                    )}
                  </div>
                  <Collapse in={Boolean(pinnedName)} timeout={500}>
                    <div
                      style={{
                        marginTop: "20px",
                        paddingTop: "20px",
                        borderTop: "1px solid #e0e0e0",
                      }}
                    >
                      <div
                        style={{
                          marginBottom: "10px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <h4 className={classes.cardTitle}>
                          {t("results.dimension_details")}
                        </h4>
                        <div
                          style={{
                            display: "flex",
                            gap: "10px",
                            alignItems: "center",
                          }}
                        >
                          <ActionDropdown
                            button={
                              <div
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 4,
                                  cursor: "pointer",
                                  padding: "3px 10px",
                                  borderRadius: 6,
                                  border: "1px solid #ddd",
                                  background: "#fff",
                                  fontSize: 11,
                                  fontWeight: 500,
                                  color: "#444",
                                  width: "200px",
                                  justifyContent: "space-between",
                                  opacity:
                                    !metaLoaded || pinnedDimensionLoading
                                      ? 0.5
                                      : 1,
                                  pointerEvents:
                                    !metaLoaded || pinnedDimensionLoading
                                      ? "none"
                                      : "auto",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 4,
                                  }}
                                >
                                  <Layers style={{ fontSize: 14 }} />
                                  <span>
                                    {pinnedDimension
                                      ? filtersNames.current[pinnedDimension]?.[
                                          i18n.language
                                        ] || pinnedDimension
                                      : t("results.select_dimension")}
                                  </span>
                                </div>
                                {pinnedDimension && (
                                  <Close
                                    style={{
                                      fontSize: 14,
                                      cursor: "pointer",
                                      opacity: 0.6,
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setPinnedDimension(null);
                                      setPinnedDimensionData({});
                                    }}
                                  />
                                )}
                              </div>
                            }
                            dropdownList={filtersList.current.map((filter) => ({
                              identified: filter,
                              text:
                                filtersNames.current[filter]?.[i18n.language] ||
                                filter,
                              active: pinnedDimension === filter,
                            }))}
                            onClick={(filter) => {
                              setPinnedDimension(filter);
                              fetchDimensionDetail(
                                filter,
                                pinnedDimensionLimit,
                              );
                            }}
                            dropPlacement="bottom-start"
                          />
                          <ActionDropdown
                            button={
                              <div
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 4,
                                  cursor: "pointer",
                                  padding: "3px 10px",
                                  borderRadius: 6,
                                  border: "1px solid #ddd",
                                  background: "#fff",
                                  fontSize: 11,
                                  fontWeight: 500,
                                  color: "#444",
                                  opacity:
                                    !metaLoaded || pinnedDimensionLoading
                                      ? 0.5
                                      : 1,
                                  pointerEvents:
                                    !metaLoaded || pinnedDimensionLoading
                                      ? "none"
                                      : "auto",
                                }}
                              >
                                <FormatListNumbered style={{ fontSize: 14 }} />
                                <span>
                                  {pinnedDimensionLimit === 500
                                    ? t("results.all_items_limited", {
                                        n: 500,
                                      })
                                    : t("results.top_n_items", {
                                        n: pinnedDimensionLimit,
                                      })}
                                </span>
                              </div>
                            }
                            dropdownList={[
                              {
                                identified: 10,
                                text: t("results.top_n_items", { n: 10 }),
                                active: pinnedDimensionLimit === 10,
                              },
                              {
                                identified: 20,
                                text: t("results.top_n_items", { n: 20 }),
                                active: pinnedDimensionLimit === 20,
                              },
                              {
                                identified: 50,
                                text: t("results.top_n_items", { n: 50 }),
                                active: pinnedDimensionLimit === 50,
                              },
                              {
                                identified: 500,
                                text: t("results.all_items_limited", {
                                  n: 500,
                                }),
                                active: pinnedDimensionLimit === 500,
                              },
                            ]}
                            onClick={(limit) => {
                              setPinnedDimensionLimit(limit);
                              if (pinnedDimension) {
                                fetchDimensionDetail(pinnedDimension, limit);
                              }
                            }}
                            dropPlacement="bottom-start"
                          />
                        </div>
                      </div>
                      <PnlDimensionDetail
                        pinnedName={pinnedName}
                        pinnedLineName={
                          plStructure.current.find(
                            (line) => line.meta_name === pinnedName,
                          )?.name
                        }
                        pinnedDimension={pinnedDimension}
                        pinnedDimensionData={pinnedDimensionData}
                        pinnedDimensionLoading={pinnedDimensionLoading}
                        filtersNames={filtersNames}
                        getMonthsListFromLastMonth={getMonthsListFromLastMonth}
                        plsValues={plsValues}
                        netRevenueAnchor={netRevenueAnchor}
                        serveMarginAnchor={serveMarginAnchor}
                        revenueAnchors={revenueAnchors.current}
                        volumeUnit={volumeUnit}
                        volumeL12M={volumeL12M}
                        currencyUnit={currencyUnit}
                        showValueType={showValueType}
                        oom={oom}
                        drillDownsOpen={drillDownsOpen}
                        setDrillDownsOpen={setDrillDownsOpen}
                        metaLoaded={metaLoaded}
                        classes={classes}
                        i18n={i18n}
                        t={t}
                      />
                      {!pinnedDimension && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "80px",
                            marginBottom: "20px",
                            padding: "30px 20px",
                            backgroundColor: "#f5f5f5",
                            border: "2px dashed #ddd",
                            borderRadius: "8px",
                            color: "#999",
                            fontSize: "13px",
                            fontStyle: "italic",
                          }}
                        >
                          <Info style={{ fontSize: 18, opacity: 0.6 }} />
                          <span>
                            {t("results.select_dimension_placeholder")}
                          </span>
                        </div>
                      )}
                    </div>
                  </Collapse>
                </CardBody>
              </Card>
            )}
          </GridItem>
        </GridContainer>
      </div>
      <FullPageLoader isVisible={!metaLoaded} />
    </>
  );
}
